import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, AlertCircle, Figma, Check } from 'lucide-react';
import { fetchFigmaFile, parseFigmaFile, parseFigmaFileHybrid } from '../utils/figmaImport';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";

export default function FigmaImportDialog({ open, onOpenChange, onImport }) {
    const [step, setStep] = useState(1); // 1: Credentials, 2: Selection
    const [token, setToken] = useState('');
    const [fileId, setFileId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [foundScreens, setFoundScreens] = useState([]);
    const [selectedScreenIds, setSelectedScreenIds] = useState([]);

    // Load token from localStorage if available
    useEffect(() => {
        const savedToken = localStorage.getItem('figma_token');
        if (savedToken) setToken(savedToken);
    }, []);

    const handleFetch = async () => {
        if (!token || !fileId) {
            setError('Please provide both a Personal Access Token and File ID');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Save token for future use
            localStorage.setItem('figma_token', token);

            const fileData = await fetchFigmaFile(fileId, token);

            // Always use hybrid mode for best of both worlds
            const screens = await parseFigmaFileHybrid(fileData, fileId, token);

            if (screens.length === 0) {
                setError('No top-level Frames found in this file. Make sure your designs are inside Frames.');
            } else {
                setFoundScreens(screens);
                // Select all by default
                setSelectedScreenIds(screens.map(s => s.id));
                setStep(2);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch Figma file');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = () => {
        const screensToImport = foundScreens.filter(s => selectedScreenIds.includes(s.id));
        onImport(screensToImport);
        onOpenChange(false);
        toast.success(`Imported ${screensToImport.length} screens from Figma`);

        // Reset state after a delay
        setTimeout(() => {
            setStep(1);
            setFoundScreens([]);
            setSelectedScreenIds([]);
            setFileId('');
        }, 500);
    };

    const toggleScreenSelection = (id) => {
        setSelectedScreenIds(prev =>
            prev.includes(id)
                ? prev.filter(sId => sId !== id)
                : [...prev, id]
        );
    };

    const toggleAll = () => {
        if (selectedScreenIds.length === foundScreens.length) {
            setSelectedScreenIds([]);
        } else {
            setSelectedScreenIds(foundScreens.map(s => s.id));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Figma className="h-5 w-5" />
                        Import from Figma
                    </DialogTitle>
                    <DialogDescription>
                        Import designs from Figma with pixel-perfect backgrounds and fully editable elements.
                    </DialogDescription>
                </DialogHeader>

                {step === 1 ? (
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="token">Personal Access Token</Label>
                            <Input
                                id="token"
                                type="password"
                                placeholder="figd_..."
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                            />
                            <p className="text-[10px] text-muted-foreground">
                                Get this from Figma Settings &gt; Account &gt; Personal Access Tokens
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="fileId">File ID</Label>
                            <Input
                                id="fileId"
                                placeholder="e.g. 8Hk3..."
                                value={fileId}
                                onChange={(e) => setFileId(e.target.value)}
                            />
                            <p className="text-[10px] text-muted-foreground">
                                Found in the URL: figma.com/file/<strong>FILE_ID</strong>/Title
                            </p>
                        </div>



                        {error && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4 py-4">
                        <div className="flex items-center justify-between">
                            <Label>Select Screens to Import</Label>
                            <Button variant="ghost" size="sm" onClick={toggleAll} className="h-auto py-1 text-xs">
                                {selectedScreenIds.length === foundScreens.length ? 'Deselect All' : 'Select All'}
                            </Button>
                        </div>

                        <ScrollArea className="h-[300px] border rounded-md p-4">
                            <div className="space-y-3">
                                {foundScreens.map(screen => (
                                    <div key={screen.id} className="flex items-start space-x-3 p-2 hover:bg-muted/50 rounded-md transition-colors">
                                        <Checkbox
                                            id={screen.id}
                                            checked={selectedScreenIds.includes(screen.id)}
                                            onCheckedChange={() => toggleScreenSelection(screen.id)}
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <label
                                                htmlFor={screen.id}
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                            >
                                                {screen.name}
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                {screen.width}x{screen.height} • {screen.elements.length} elements
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>

                        <div className="text-xs text-muted-foreground text-center">
                            Screens are imported with background images and all elements as editable overlays.
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {step === 1 ? (
                        <Button onClick={handleFetch} disabled={loading} className="w-full sm:w-auto">
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Fetch Screens
                        </Button>
                    ) : (
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                            <Button onClick={handleImport} disabled={selectedScreenIds.length === 0}>
                                Import {selectedScreenIds.length} Screens
                            </Button>
                        </div>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
